import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Typography,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { QuotationService } from '../../services/quotation.service';
import { Quotation, QUOTATION_STATUS_COLORS } from '../../interfaces/sales/quotation.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { useAuth } from '../../context/AuthContext';
import { QUOTATION_CREATE_ROLES } from '../../constants/salesRoles';

const QuotationList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const userRoles: string[] = user && user.roles ? (Array.isArray(user.roles) ? user.roles : [user.roles]) : [];
  // The POST endpoint 403s every other role (e.g. InstitutionManager, who can still read the list).
  const canCreate = QUOTATION_CREATE_ROLES.some(role => userRoles.includes(role));
  const [rows, setRows] = useState<Quotation[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(true);

  useEffect(() => { load(); }, [page, size]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const res = await QuotationService.listByInstitution(institutionId, page, size);
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.showApiError(e, 'Failed to load quotations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Quotations</Typography>
        {canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sales/quotations/new')}>
            New Quotation
          </Button>
        )}
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Quotation #</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Valid Until</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow
                  key={row.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/sales/quotations/${row.id}`)}
                >
                  <TableCell>{row.quotationNumber ?? 'Draft'}</TableCell>
                  <TableCell>{row.customerName ?? '—'}</TableCell>
                  <TableCell>{row.quotationDate}</TableCell>
                  <TableCell>{row.validUntil}</TableCell>
                  <TableCell>{row.totalAmount}</TableCell>
                  <TableCell>
                    <Chip
                      label={row.status}
                      color={QUOTATION_STATUS_COLORS[row.status]}
                      size="small"
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
          />
        </TableContainer>
      )}
    </Box>
  );
};

export default QuotationList;
